import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { SelectList } from "@common/selectList";
import { Calendar } from "@common/calendar";
import { Props } from "./types";
import styles from "./styles.scss";

function DateTimeInput({
  dateTime,
  projectTimezone,
  placeholder,
  isInvalid,
  isDisabled,
  readOnly,
  input,
}: Props) {
  const { t } = useTranslation();
  const display = dateTime
    ? DateTime.fromISO(dateTime, { zone: "utc" }).setZone(projectTimezone)
    : null;
  const isValid = !!display && display.isValid;

  const commitDateTime = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
  ) => {
    const next = DateTime.fromObject(
      { year, month, day, hour, minute, second: 0, millisecond: 0 },
      { zone: projectTimezone },
    );
    if (!next.isValid) return;
    input(next.toUTC().toISO() ?? "");
  };

  return readOnly ? (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: isInvalid,
        [styles.disabled]: isDisabled,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", {
        timezone: projectTimezone,
      })}
    >
      <div className={styles.calendarInput}>
        <div className={styles.input}>
          {isValid ? (
            <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
          ) : (
            <p className={styles.placeholder}>{placeholder}</p>
          )}
        </div>
      </div>
      <div className={styles.input}>
        {isValid ? (
          <p className={styles.value}>{display.toFormat("HH:mm")}</p>
        ) : (
          <p className={styles.placeholder}>00:00</p>
        )}
      </div>
    </div>
  ) : (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: isInvalid,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", {
        timezone: projectTimezone,
      })}
    >
      <SelectList<string>
        rootClassName={classNames(styles.calendarInput, {
          [styles.invalid]: isInvalid,
          [styles.disabled]: isDisabled,
        })}
        listClassName={styles.calendar}
      >
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              {isValid ? (
                <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
              ) : (
                <p className={styles.placeholder}>{placeholder}</p>
              )}
            </div>
          ),
          list: (
            <Calendar
              date={
                isValid
                  ? {
                      year: display.year,
                      month: display.month - 1,
                      day: display.day,
                    }
                  : (() => {
                      const now = DateTime.now().setZone(projectTimezone);
                      return {
                        year: now.year,
                        month: now.month - 1,
                        day: now.day,
                      };
                    })()
              }
              editDate={({ year, month, day }) => {
                const hour = isValid ? display.hour : 0;
                const minute = isValid ? display.minute : 0;
                commitDateTime(year, month + 1, day, hour, minute);
                select("");
              }}
            />
          ),
        })}
      </SelectList>
    </div>
  );
}

export { DateTimeInput };
